import { authLogout, exchangeSutForTokenV2, getAuthStatus } from '@helios/frontend-api-v2-client';
import { useQueryClient as useTanStackQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { loginUser, LoginUser, LoginUserResponse, refreshUserToken } from './api/login';
import {
  checkBiometrics,
  clearCredentialStorage,
  clearToken,
  getStoredCredentials,
  getToken,
  hasStoredCredentials,
  removeImpersonationCookie,
} from './auth';
import { getFriendlyLoginError } from './error';

import authContext from './authContext';

export interface Props {
  children?: React.ReactNode;
}

const AuthProvider: React.FC<Props> = ({ children }: Props) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(getToken()));
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [biometricsError, setBiometricsError] = useState<string>();
  const queryClient = useQueryClient();
  const tanStackQueryClient = useTanStackQueryClient();

  const { mutate: login, error: loginError } = useMutation<LoginUserResponse, AxiosError, LoginUser>(loginUser, {
    onSuccess: () => {
      setIsAuthenticated(true);
    },
    onSettled: () => {
      setIsAuthLoading(false);
    },
  });

  const { mutate: refresh, error: refreshError } = useMutation<
    LoginUserResponse,
    AxiosError,
    { refreshToken: string }
  >(refreshUserToken, {
    onSuccess: () => {
      setIsAuthenticated(true);
    },
    onSettled: () => {
      setIsAuthLoading(false);
    },
  });

  const logout = () => {
    authLogout();
    setIsAuthLoading(true);
    setTimeout(() => {
      clearToken();
      clearCredentialStorage();
      removeImpersonationCookie();
      setIsAuthenticated(false);
      setIsAuthLoading(false);
    }, 0);
  };

  useEffect(() => {
    const initAuth = async () => {
      const sut = new URLSearchParams(window.location.search).get('sut');
      if (sut) {
        try {
          await exchangeSutForTokenV2({ query: { sut } });
        } catch {
          // fall through to cookie check
        }
        const url = new URL(window.location.href);
        url.searchParams.delete('sut');
        window.history.replaceState(window.history.state, '', url);
      }

      try {
        const { data } = await getAuthStatus();
        if (data?.authenticated) {
          setIsAuthenticated(true);
          setIsAuthLoading(false);
          return;
        }
      } catch {
        // fall through to localStorage check
      }

      const hasAuth = Boolean(getToken());
      if (hasAuth) {
        setIsAuthenticated(true);
        setIsAuthLoading(false);
        return;
      }

      if ((await hasStoredCredentials()) && (await checkBiometrics())) {
        const creds = await getStoredCredentials();
        try {
          if (creds) {
            if ('refreshToken' in creds) {
              refresh(creds);
            } else {
              login(creds);
            }
            return;
          }
        } catch {
          await clearCredentialStorage();
          setBiometricsError('Sorry, but you are unable to log in with biometrics at this time');
        }
      }

      setIsAuthenticated(false);
      setIsAuthLoading(false);
    };
    initAuth();
  }, []);

  useEffect(() => {
    const removeAllQueries = () => {
      queryClient.removeQueries();
      tanStackQueryClient.removeQueries();
    };
    if (!isAuthenticated) {
      removeAllQueries();
    }
    return removeAllQueries;
  }, [isAuthenticated, queryClient, tanStackQueryClient]);

  const friendlyLoginError = getFriendlyLoginError(loginError || refreshError);

  const value = React.useMemo(
    () => ({
      login,
      logout,
      isAuthLoading,
      loginError: friendlyLoginError || biometricsError,
      isAuthenticated,
      setIsAuthenticated,
    }),
    [biometricsError, isAuthenticated, isAuthLoading, login, friendlyLoginError, setIsAuthenticated],
  );

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
};

export default AuthProvider;
